import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import Lock from '@mui/icons-material/Lock';
import LockOpen from '@mui/icons-material/LockOpen';
import { useGetSalesOrdersQuery, useDeleteSalesOrderMutation } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import PinPromptDialog from '../../../shared/components/PinPromptDialog';

function SalesOrdersList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data: company } = useGetCurrentCompanyQuery();
  const isPinActive = Boolean(company?.enableSecurityPin && company?.securityPin);
  const [amountsUnlocked, setAmountsUnlocked] = useState(false);
  const [pinDialogState, setPinDialogState] = useState({
    open: false,
    target: null, // 'unlockAmounts' | 'viewOrder' | 'newOrder'
    orderId: null,
    title: 'Security PIN Required',
    description: 'Enter your security PIN to access sales details.',
  });

  const handleRequestPin = (target, orderId = null) => {
    if (!isPinActive) {
      if (target === 'viewOrder' && orderId) {
        navigate(`/sales/orders/${orderId}`);
      } else if (target === 'newOrder') {
        navigate('/sales/orders/new');
      }
      return;
    }
    let title = 'Security PIN Required';
    let description = 'Enter your security PIN to proceed.';
    if (target === 'viewOrder') {
      title = 'View Sales Invoice';
      description = 'Enter your security PIN to view invoice details.';
    } else if (target === 'unlockAmounts') {
      title = 'Unlock Sales Amounts';
      description = 'Enter your security PIN to reveal total and balance amounts.';
    }
    setPinDialogState({
      open: true,
      target,
      orderId,
      title,
      description,
    });
  };

  const handlePinSuccess = () => {
    const { target, orderId } = pinDialogState;
    if (target === 'unlockAmounts') {
      setAmountsUnlocked(true);
    } else if (target === 'viewOrder' && orderId) {
      navigate(`/sales/orders/${orderId}`);
    } else if (target === 'newOrder') {
      navigate('/sales/orders/new');
    }
    setPinDialogState({ open: false, target: null, orderId: null, title: '', description: '' });
  };

  const toApiDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
          d.getDate(),
        ).padStart(2, '0')}`
      : undefined;

  const { data, isLoading, error } = useGetSalesOrdersQuery({
    page,
    limit: rowsPerPage,
    status: statusFilter || undefined,
    search: search || undefined,
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
  });

  const [deleteSalesOrder, { isLoading: isDeleting }] = useDeleteSalesOrderMutation();
  
  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
        maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (dateValue, timeValue) => {
    if (!dateValue) return '-';
    const dDate = new Date(dateValue);
    if (Number.isNaN(dDate.getTime())) return '-';
    const dTime = timeValue ? new Date(timeValue) : dDate;
    const dd = String(dDate.getDate()).padStart(2, '0');
    const mm = String(dDate.getMonth() + 1).padStart(2, '0');
    const yyyy = dDate.getFullYear();
    let hours = dTime.getHours();
    const minutes = String(dTime.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const timeStr = `${hours}:${minutes} ${ampm}`;
    let dateStr = '';
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        dateStr = `${mm}/${dd}/${yyyy}`;
        break;
      case 'yyyy-MM-dd':
        dateStr = `${yyyy}-${mm}-${dd}`;
        break;
      case 'dd-MM-yyyy':
        dateStr = `${dd}-${mm}-${yyyy}`;
        break;
      case 'dd/MM/yyyy':
      default:
        dateStr = `${dd}/${mm}/${yyyy}`;
    }
    return `${dateStr} ${timeStr}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      confirmed: 'info',
      shipped: 'warning',
      delivered: 'success',
      cancelled: 'error',
      on_hold: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    if (status === 'on_hold') return 'On hold';
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : status;
  };

  const handleOpenDelete = (order) => {
    setSelectedOrder(order);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setSelectedOrder(null);
    setDeleteOpen(false);
  };

  const confirmDelete = async () => {
    if (!selectedOrder) return;
    try {
      await deleteSalesOrder(selectedOrder.id).unwrap();
      alert('Sales order deleted and inventory reversed successfully');
      handleCloseDelete();
    } catch (err) {
      alert(err?.data?.message || 'Failed to delete sales order');
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading sales orders</Typography>;

  const orders = data?.salesOrders || [];
  const pagination = data?.pagination;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Sales
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {isPinActive && (
            <Button
              variant="outlined"
              size="small"
              color={amountsUnlocked ? 'warning' : 'inherit'}
              startIcon={amountsUnlocked ? <LockOpen /> : <Lock />}
              onClick={() => {
                if (amountsUnlocked) {
                  setAmountsUnlocked(false);
                } else {
                  handleRequestPin('unlockAmounts');
                }
              }}
              sx={{ borderRadius: 2 }}
            >
              {amountsUnlocked ? 'Lock Amounts' : 'Unlock Amounts'}
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate('/sales/returns')} sx={{ borderRadius: 2 }}>
            Returns
          </Button>
          <Button variant="outlined" onClick={() => navigate('/sales/print')} sx={{ borderRadius: 2 }}>
            Print List
          </Button>
          <Button variant="outlined" onClick={() => navigate('/customers')} sx={{ borderRadius: 2 }}>
            Customers
          </Button>
          <Button variant="outlined" onClick={() => navigate('/sales/holds')} sx={{ borderRadius: 2 }}>
            Holds
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')} sx={{ borderRadius: 2 }}>
            New Sales Order
          </Button>
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card>
          <CardContent>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField
                  size="small"
                  label="Search (order / invoice)"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  size="small"
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="on_hold">On hold</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
                <DatePicker
                  label="From date"
                  value={fromDate}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="To date"
                  value={toDate}
                  onChange={(newValue) => {
                    setToDate(newValue);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Box>
              {pagination && (
                <Typography variant="body2" color="text.secondary">
                  {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
                </Typography>
              )}
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Total Amount (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sales Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Balance Amount (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length > 0 ? (
                    orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell
                          sx={{ fontWeight: 500, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                          onClick={() => handleRequestPin('viewOrder', order.id)}
                        >
                          {order.orderNumber}
                        </TableCell>
                        <TableCell
                          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                          onClick={() => handleRequestPin('viewOrder', order.id)}
                        >
                          {order.invoiceNumber || '-'}
                        </TableCell>
                        <TableCell>{order.customer?.name || '-'}</TableCell>
                        <TableCell>{formatDate(order.orderDate, order.createdAt)}</TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: isPinActive && !amountsUnlocked ? 'text.secondary' : 'success.main',
                            cursor: isPinActive && !amountsUnlocked ? 'pointer' : 'default',
                          }}
                          onClick={isPinActive && !amountsUnlocked ? () => handleRequestPin('unlockAmounts') : undefined}
                          title={isPinActive && !amountsUnlocked ? 'Click to enter PIN and unlock amounts' : ''}
                        >
                          {isPinActive && !amountsUnlocked ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <Lock sx={{ fontSize: 14 }} color="action" />
                              <span>••••••</span>
                            </Box>
                          ) : (
                            formatAmount(order.totalAmount)
                          )}
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{order.salesType}</TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            color:
                              isPinActive && !amountsUnlocked
                                ? 'text.secondary'
                                : Number(order.balanceAmount || 0) > 0
                                ? 'warning.main'
                                : 'text.secondary',
                            cursor: isPinActive && !amountsUnlocked ? 'pointer' : 'default',
                          }}
                          onClick={isPinActive && !amountsUnlocked ? () => handleRequestPin('unlockAmounts') : undefined}
                          title={isPinActive && !amountsUnlocked ? 'Click to enter PIN and unlock amounts' : ''}
                        >
                          {isPinActive && !amountsUnlocked ? (
                            <span>••••••</span>
                          ) : Number(order.balanceAmount || 0) > 0 ? (
                            formatAmount(order.balanceAmount)
                          ) : (
                            '–'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(order.status)}
                            color={getStatusColor(order.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRequestPin('viewOrder', order.id)}
                              title="View Order"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDelete(order)}
                              title="Delete Order"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No sales orders found</Typography>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')} sx={{ mt: 2 }}>
                          Create First Sales Order
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {pagination && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
                </Typography>
                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={(pagination.page || 1) - 1}
                  onPageChange={(_, newPage) => setPage(newPage + 1)}
                  rowsPerPage={pagination.limit || rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    const next = parseInt(e.target.value, 10) || 10;
                    setRowsPerPage(next);
                    setPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </LocalizationProvider>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleCloseDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete sales order{' '}
            <strong>{selectedOrder?.orderNumber || selectedOrder?.invoiceNumber}</strong>?
            <br />
            <br />
            This action is permanent. Stock quantities will be reversed, and accounting entries associated with this sale will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            autoFocus
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PIN Prompt Dialog */}
      <PinPromptDialog
        open={pinDialogState.open}
        onClose={() => setPinDialogState({ open: false, target: null, orderId: null, title: '', description: '' })}
        onSuccess={handlePinSuccess}
        expectedPin={company?.securityPin}
        title={pinDialogState.title}
        description={pinDialogState.description}
      />
    </Box>
  );
}

export default SalesOrdersList;
